import React, { useState, useEffect } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/card';

export default function ApiKeyManagement() {
    const [apiKeys, setApiKeys] = useState([]);
    const [newKeyName, setNewKeyName] = useState('');
    const [generatedKey, setGeneratedKey] = useState(null);

    useEffect(() => {
        fetchApiKeys();
    }, []);

    const fetchApiKeys = async () => {
        try {
            const token = localStorage.getItem('token');
            const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/api-keys`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (res.ok) {
                const data = await res.json();
                setApiKeys(data);
            }
        } catch (error) {
            console.error('Error fetching API keys:', error);
        }
    };

    const handleCreateKey = async (e) => {
        e.preventDefault();
        try {
            const token = localStorage.getItem('token');
            const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/api-keys`, {
                method: 'POST',
                headers: { 
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ name: newKeyName })
            });
            if (res.ok) {
                const data = await res.json();
                setGeneratedKey(data.key);
                setNewKeyName('');
                fetchApiKeys();
            }
        } catch (error) {
            console.error('Error creating API key:', error);
        }
    };

    const handleDeleteKey = async (id) => {
        try {
            const token = localStorage.getItem('token');
            const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/api-keys/${id}`, {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (res.ok) {
                fetchApiKeys();
                if (generatedKey && apiKeys.find(k => k.id === id)?.key === generatedKey) {
                    setGeneratedKey(null);
                }
            }
        } catch (error) {
            console.error('Error deleting API key:', error);
        }
    };

    return (
        <div className="space-y-6">
            <Card>
                <CardHeader>
                    <CardTitle>API Key Management</CardTitle>
                </CardHeader>
                <CardContent>
                    <p className="text-gray-400 mb-6">Generate API keys to authenticate CLI tools and external services.</p>
                    
                    <form onSubmit={handleCreateKey} className="flex gap-4 mb-8">
                        <input
                            type="text"
                            placeholder="Key Name (e.g., CLI Key)"
                            value={newKeyName}
                            onChange={(e) => setNewKeyName(e.target.value)}
                            className="bg-gray-800 text-white rounded-lg px-4 py-2 flex-grow border border-gray-700"
                            required
                        />
                        <button type="submit" className="bg-[#0f5d9a] hover:bg-blue-600 text-white px-4 py-2 rounded-lg">
                            Generate Key
                        </button>
                    </form>

                    {generatedKey && (
                        <div className="bg-green-900/30 border border-green-500 rounded-lg p-4 mb-8">
                            <h3 className="text-green-400 font-bold mb-2">New API Key Generated!</h3>
                            <p className="text-gray-300 mb-2">Please copy this key now. You won't be able to see it again.</p>
                            <code className="bg-black text-green-400 px-4 py-2 rounded block w-full overflow-x-auto select-all">
                                {generatedKey}
                            </code>
                        </div>
                    )}

                    <div className="bg-gray-900 rounded-lg overflow-hidden border border-gray-800">
                        <table className="w-full text-left">
                            <thead className="bg-gray-800">
                                <tr>
                                    <th className="p-4 text-gray-400 font-medium">Name</th>
                                    <th className="p-4 text-gray-400 font-medium">Prefix</th>
                                    <th className="p-4 text-gray-400 font-medium">Created At</th>
                                    <th className="p-4 text-right text-gray-400 font-medium">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {apiKeys.map((key) => (
                                    <tr key={key.id} className="border-t border-gray-800">
                                        <td className="p-4 text-white">{key.name}</td>
                                        <td className="p-4 text-gray-400 font-mono text-sm">
                                            {key.key.substring(0, 8)}...
                                        </td>
                                        <td className="p-4 text-gray-400">
                                            {new Date(key.createdAt).toLocaleDateString()}
                                        </td>
                                        <td className="p-4 text-right">
                                            <button 
                                                onClick={() => handleDeleteKey(key.id)}
                                                className="text-red-400 hover:text-red-300 px-3 py-1 bg-red-400/10 hover:bg-red-400/20 rounded"
                                            >
                                                Revoke
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                                {apiKeys.length === 0 && (
                                    <tr>
                                        <td colSpan="4" className="p-4 text-center text-gray-500">
                                            No API keys generated yet.
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </CardContent>
            </Card>
        </div>
    );
}
